import { Property, User, Agent, School, NeighborhoodData, MultilingualText } from './types';

// Import JSON data
import agentsData from './data/agents.json';
import schoolsData from './data/schools.json';
import propertiesData from './data/properties.json';
import mockUserData from './data/mockUser.json';
import filterOptionsData from './data/filterOptions.json';

// Transform agents data to match the new structure
export const mockAgents: Agent[] = agentsData.map(agent => ({
  ...agent,
  name: agent.name,
  bio: agent.bio,
  specialties: agent.specialties,
  serviceAreas: agent.serviceAreas
}));

// Define the type for JSON school data
interface JsonSchool {
  id: string;
  name: MultilingualText;
  type: string; // This could be any string from JSON
  rating: number;
  grades: string;
  distance: number;
  studentCount: number;
  address: string;
}

// Schools by region
const schoolsByRegion: Record<string, School[]> = {};
Object.keys(schoolsData).forEach((region: string) => {
  schoolsByRegion[region] = schoolsData[region as keyof typeof schoolsData].map((school: JsonSchool) => ({
    ...school,
    type: school.type as 'Elementary' | 'Middle' | 'High' // Assert the type
  }));
});

// Helper function to create neighborhood data
const createNeighborhoodData = (
  walkScore: number,
  transitScore: number,
  bikeScore: number,
  schools: School[],
  medianHomeValue: number,
  medianRent: number,
  crimeRate: 'Low' | 'Medium' | 'High',
  population: number,
  medianAge: number,
  medianIncome: number
): NeighborhoodData => ({
  walkScore,
  transitScore,
  bikeScore,
  schools,
  medianHomeValue,
  medianRent,
  crimeRate,
  demographics: {
    population,
    medianAge,
    medianIncome
  }
});

// Transform JSON properties to include school data
interface PropertyJsonData {
  id: string;
  title: MultilingualText;
  price: number;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  beds: number;
  baths: number;
  sqft: number;
  propertyType: string;
  listingType: string;
  images: string[];
  mainImage: string;
  youtubeVideoUrls?: string[];
  latitude: number;
  longitude: number;
  description: MultilingualText;
  yearBuilt: number;
  lotSize: string;
  parking: number;
  features: MultilingualText[];
  agentId: string;
  neighborhoodData: {
    walkScore: number;
    transitScore: number;
    bikeScore: number;
    schoolsKey: string;
    medianHomeValue: number;
    medianRent: number;
    crimeRate: 'Low' | 'Medium' | 'High';
    demographics: {
      population: number;
      medianAge: number;
      medianIncome: number;
    };
  };
  hoaFees?: number;
  daysOnMarket: number;
  virtualTourUrl?: string;
  openHouseDate?: string;
  // Extended property details
  condition?: string;
  orientation?: string;
  terraceArea?: number;
  gatedCommunity?: string;
  streetFrontage?: string;
  layout?: string;
  floor?: number;
  totalFloors?: number;
  petsAllowed?: string;
  minimumContract?: string;
  socialHousing?: string;
  officeSuitable?: string;
  isPenthouse?: boolean;
  financingAvailable?: string;
  // Interior facts
  fullBaths?: number;
  halfBaths?: number;
  heatingTypes?: MultilingualText[];
  coolingTypes?: MultilingualText[];
  appliances?: MultilingualText[];
  laundryFeatures?: MultilingualText;
  interiorFeatures?: MultilingualText[];
  livableArea?: number;
  flooring?: MultilingualText[];
  // Property facts
  parkingFeatures?: MultilingualText[];
  garageType?: string;
  coveredSpaces?: number;
  stories?: number;
  patioAndPorch?: MultilingualText[];
  fencing?: MultilingualText;
  lotFeatures?: MultilingualText[];
  additionalStructures?: MultilingualText[];
  // Construction facts
  propertySubType?: MultilingualText;
  architecturalStyle?: MultilingualText;
  constructionMaterials?: MultilingualText[];
  foundation?: MultilingualText;
  roof?: MultilingualText;
  isNewConstruction?: boolean;
  sold?: boolean;
}

export const mockProperties: Property[] = (propertiesData as PropertyJsonData[]).map((prop) => ({
  id: prop.id,
  title: prop.title,
  price: prop.price,
  address: prop.address,
  city: prop.city,
  state: prop.state,
  zipCode: prop.zipCode,
  beds: prop.beds,
  baths: prop.baths,
  sqft: prop.sqft,
  propertyType: prop.propertyType,
  listingType: prop.listingType,
  images: prop.images,
  mainImage: prop.mainImage,
  youtubeVideoUrls: prop.youtubeVideoUrls,
  latitude: prop.latitude,
  longitude: prop.longitude,
  description: prop.description,
  yearBuilt: prop.yearBuilt,
  lotSize: prop.lotSize,
  parking: prop.parking,
  features: prop.features,
  agentId: prop.agentId,
  neighborhoodData: createNeighborhoodData(
    prop.neighborhoodData.walkScore,
    prop.neighborhoodData.transitScore,
    prop.neighborhoodData.bikeScore,
    schoolsByRegion[prop.neighborhoodData.schoolsKey] || [],
    prop.neighborhoodData.medianHomeValue,
    prop.neighborhoodData.medianRent,
    prop.neighborhoodData.crimeRate,
    prop.neighborhoodData.demographics.population,
    prop.neighborhoodData.demographics.medianAge,
    prop.neighborhoodData.demographics.medianIncome
  ),
  hoaFees: prop.hoaFees,
  daysOnMarket: prop.daysOnMarket,
  virtualTourUrl: prop.virtualTourUrl,
  openHouseDate: prop.openHouseDate,
  // Extended property details
  condition: prop.condition,
  orientation: prop.orientation,
  terraceArea: prop.terraceArea,
  gatedCommunity: prop.gatedCommunity,
  streetFrontage: prop.streetFrontage,
  layout: prop.layout,
  floor: prop.floor,
  totalFloors: prop.totalFloors,
  petsAllowed: prop.petsAllowed,
  minimumContract: prop.minimumContract,
  socialHousing: prop.socialHousing,
  officeSuitable: prop.officeSuitable,
  isPenthouse: prop.isPenthouse,
  financingAvailable: prop.financingAvailable,
  // Interior facts
  fullBaths: prop.fullBaths,
  halfBaths: prop.halfBaths,
  heatingTypes: prop.heatingTypes,
  coolingTypes: prop.coolingTypes,
  appliances: prop.appliances,
  laundryFeatures: prop.laundryFeatures,
  interiorFeatures: prop.interiorFeatures,
  livableArea: prop.livableArea,
  flooring: prop.flooring,
  // Property facts
  parkingFeatures: prop.parkingFeatures,
  garageType: prop.garageType,
  coveredSpaces: prop.coveredSpaces,
  stories: prop.stories,
  patioAndPorch: prop.patioAndPorch,
  fencing: prop.fencing,
  lotFeatures: prop.lotFeatures,
  additionalStructures: prop.additionalStructures,
  // Construction facts
  propertySubType: prop.propertySubType,
  architecturalStyle: prop.architecturalStyle,
  constructionMaterials: prop.constructionMaterials,
  foundation: prop.foundation,
  roof: prop.roof,
  isNewConstruction: prop.isNewConstruction,
  sold: prop.sold,
}));

export const mockUser: User = mockUserData;

// Transform filter options to handle Infinity values
export const filterOptions = {
  propertyTypes: filterOptionsData.propertyTypes,
  priceRanges: filterOptionsData.priceRanges.map((range) => ({
    label: range.label,
    min: range.min,
    max: range.max === null ? Infinity : range.max
  })),
  bedrooms: filterOptionsData.bedrooms,
  bathrooms: filterOptionsData.bathrooms,
  sqftRanges: filterOptionsData.sqftRanges.map((range) => ({
    label: range.label,
    min: range.min,
    max: range.max === null ? Infinity : range.max
  })),
  lotSizes: filterOptionsData.lotSizes,
  daysOnMarket: filterOptionsData.daysOnMarket
};
